'use client';

/**
 * discoverPrefetch.ts
 *
 * A lightweight in-memory prefetch cache for Discover data.
 * - Populated non-blockingly from the Home page after auth resolves.
 * - Consumed by DiscoverScreen on first mount if still fresh.
 * - Falls back silently if cache is empty, stale, or errored.
 * - Never throws; never blocks any page render.
 */

import { jsnService } from '@/lib/supabase/jsnService';
import type { MemberProfile, PreviewProfile } from '@/lib/supabase/types';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface DiscoverPrefetchData {
  profiles: MemberProfile[];
  previews: PreviewProfile[];
  likedIds: string[];
  passedIds: string[];
  fetchedAt: number;
}

// Module-level singleton — survives client-side navigation
let cache: DiscoverPrefetchData | null = null;
let inflight: Promise<void> | null = null;

/** Returns cached data if it exists and is within TTL, otherwise null. */
export function getDiscoverCache(): DiscoverPrefetchData | null {
  if (!cache) return null;
  if (Date.now() - cache.fetchedAt > CACHE_TTL_MS) {
    cache = null;
    return null;
  }
  return cache;
}

/** Clears the cache (e.g. after a like/pass action so next visit re-fetches). */
export function clearDiscoverCache(): void {
  cache = null;
  inflight = null;
}

/**
 * Triggers a non-blocking background prefetch of Discover data.
 * Safe to call multiple times — deduplicates via inflight promise.
 * Never rejects; errors are swallowed silently.
 */
export function prefetchDiscoverData(userId: string): void {
  // Already cached and fresh — nothing to do
  if (getDiscoverCache()) return;
  // Already in-flight — don't double-fetch
  if (inflight) return;

  inflight = (async () => {
    try {
      const [profiles, previews, likedIds, passedIds] = await Promise.all([
        jsnService.getDiscoverProfiles(userId),
        jsnService.getVisiblePreviewProfiles(),
        jsnService.getLikedProfileIds(userId),
        jsnService.getPassedProfileIds(userId),
      ]);
      const previewsWithPhotos = await jsnService.enrichVisiblePreviewPhotos(previews);
      cache = { profiles, previews: previewsWithPhotos, likedIds, passedIds, fetchedAt: Date.now() };
    } catch {
      // Prefetch failed — Discover will load normally on its own
      cache = null;
    } finally {
      inflight = null;
    }
  })();
}
